import { useFileListStore } from '@/stores/FileList.store'
import { useProductOrderStore } from '@/features/productOrder/ProductOrder.store'

// Shorthands for product.fileType, so the long Office mime types don't have to be
// typed out in the product JSON. Mime types match RijnjaMediaType in media-server.
const FILE_TYPE_ALIASES = {
  pdf: ['application/pdf'],
  image: ['image/*'],
  word: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ],
  excel: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ],
  csv: ['text/csv'],
  text: ['text/plain'],
  zip: ['application/zip']
}

// Sources a product can derive its quantity from, instead of letting the user pick one.
// Keyed by the value of product.quantitySource in the product JSON. The getter reads a
// value that the file analysis put on the print specs.
const QUANTITY_SOURCES = {
  numberOfLines: (file) => file.numberOfLines
}

// Check whether a mimeType matches one of the FILE_TYPE_ALIASES groups (or a raw
// mime type / wildcard). Exported so components outside the mixin can reuse it.
export function matchesFileType(mimeType, pattern) {
  if (!mimeType) return false

  return (FILE_TYPE_ALIASES[pattern] || [pattern]).some((candidate) =>
    candidate.endsWith('/*')
      ? mimeType.startsWith(candidate.slice(0, -1))
      : candidate === mimeType
  )
}

export default {
  data() {
    return {
      isCheckingDisabled: false,
      isCheckingHidden: false
    }
  },
  computed: {
    fileList() {
      return useFileListStore()
    },
    productOrderStore() {
      return useProductOrderStore()
    }
  },
  methods: {
    isOptionMatch(conditionName, selectedOptionName, product) {
      // Check if selectedOptionName matches conditionName directly
      // OR if selectedOptionName is a subOption of an option named conditionName
      if (conditionName === selectedOptionName) return true

      if (!product) return false

      // Check if conditionName is a parent option with subOptions
      for (const menu of product.menus) {
        for (const optionGroup of menu.optionGroups || []) {
          for (const option of optionGroup.options || []) {
            if (
              option.name === conditionName &&
              option.subOptions?.some(
                (sub) => sub.name === selectedOptionName
              )
            ) {
              return true
            }
          }
        }
      }
      return false
    },
    getActiveProduct(selectedSpecs) {
      // Get the product definition matching the current specs
      if (!selectedSpecs?.length) return null
      const file = this.fileList?.fileList?.find(
        (f) => f.productSpecs?.specs === selectedSpecs
      )
      const productTitle = file?.productSpecs?.title
      if (productTitle) {
        return this.productOrderStore?.products?.find(
          (p) => p.title === productTitle
        )
      }
      // Fallback: try to find product from any file that has specs
      const anyFile = this.fileList?.fileList?.find(
        (f) => f.productSpecs?.title
      )
      if (anyFile) {
        return this.productOrderStore?.products?.find(
          (p) => p.title === anyFile.productSpecs.title
        )
      }
      return null
    },
    parseHideWhenCondition(conditionString, selectedSpecs) {
      // Parse and evaluate hideWhen condition
      // Supports both 'OPTION_NAME' and 'optionGroupName:OPTION_NAME' formats
      // Also supports parent option names (matches if any subOption is selected)
      const product = this.getActiveProduct(selectedSpecs)

      if (conditionString.includes(':')) {
        // Format: 'optionGroupName:optionName'
        const [groupName, optionName] = conditionString.split(':')
        return selectedSpecs.some(
          (spec) =>
            spec.optionGroupName === groupName &&
            this.isOptionMatch(optionName, spec.optionName, product)
        )
      } else {
        // Format: 'optionName' - match any optionGroupName
        return selectedSpecs.some((spec) =>
          this.isOptionMatch(conditionString, spec.optionName, product)
        )
      }
    },
    // Returns true if the file has a required optionGroup (e.g. opmerking) that is not filled in.
    // Looks up the product definition to find required optionGroups.
    fileHasRequiredSpecsMissing(file) {
      const specs = file.productSpecs?.specs
      if (!specs?.length) return false

      const product = this.productOrderStore?.products?.find(
        (p) => p.title === file.productSpecs?.title
      )
      if (!product) return false

      for (const menu of product.menus) {
        for (const optionGroup of menu.optionGroups || []) {
          if (!optionGroup.required) continue
          const spec = specs.find((s) => s.optionGroupName === optionGroup.name)
          if (!spec || !spec.optionTitle) return true
        }
      }
      return false
    },
    menuHideCheck(menu, selectedFiles) {
      // Check if a menu should be hidden based on hideWhen conditions
      if (!menu.hideWhen || !menu.hideWhen.length) {
        return false
      }

      const selectedSpecs = selectedFiles[0].productSpecs.specs

      return menu.hideWhen.some((condition) =>
        this.parseHideWhenCondition(condition, selectedSpecs)
      )
    },
    checkAndRemoveHiddenMenuSpecs(selectedFileNames) {
      // Remove specs from menus that should be hidden
      const selectedFiles = this.fileList.fileList.filter((file) =>
        selectedFileNames.includes(file.fileName)
      )

      if (!selectedFiles.length) return

      const file = selectedFiles[0]
      if (!file.productSpecs || !file.productSpecs.specs) return

      // Get the product configuration from the store
      if (!this.productOrderStore) return

      const product = this.productOrderStore.products?.find(
        (p) => p.title === file.productSpecs.title
      )
      if (!product) return

      // For each menu
      product.menus.forEach((menu) => {
        // If this menu should be hidden (via hideWhen or missing required file type)
        const hiddenByHideWhen = this.menuHideCheck(menu, selectedFiles)
        const hiddenByFileType = menu.fileTypeRequired &&
          !file.productSpecs.files?.some((f) => f.type === menu.fileTypeRequired)

        if (hiddenByHideWhen || hiddenByFileType) {
          // Remove all specs from this menu
          file.productSpecs.specs = file.productSpecs.specs.filter((spec) => {
            return !(spec.title === (menu.title || ''))
          })
        }
      })
    },
    checkAndRestoreVisibleMenuDefaults(selectedFileNames) {
      // Restore default specs for menus that became visible
      const selectedFiles = this.fileList.fileList.filter((file) =>
        selectedFileNames.includes(file.fileName)
      )

      if (!selectedFiles.length) return

      const file = selectedFiles[0]
      if (!file.productSpecs || !file.productSpecs.specs) return

      // Get the product configuration from the store
      if (!this.productOrderStore) return

      const product = this.productOrderStore.products?.find(
        (p) => p.title === file.productSpecs.title
      )
      if (!product) return

      // For each menu
      product.menus.forEach((menu) => {
        // Menu is visible if not hidden by hideWhen and not missing required file type
        const hiddenByHideWhen = this.menuHideCheck(menu, selectedFiles)
        const hiddenByFileType = menu.fileTypeRequired &&
          !file.productSpecs.files?.some((f) => f.type === menu.fileTypeRequired)

        // If this menu should be visible
        if (!hiddenByHideWhen && !hiddenByFileType) {
          // Check if specs exist for this menu
          const hasSpecs = file.productSpecs.specs.some(
            (spec) => spec.title === (menu.title || '')
          )

          // If no specs exist, restore defaults
          if (!hasSpecs) {
            // Check if this menu has a toggleOptions that defaults to disabled
            const toggleGroup = menu.optionGroups?.find(
              (og) => og.name === 'toggleOptions'
            )
            const menuDisabledByToggle =
              toggleGroup && toggleGroup.default === 'disabled'

            menu.optionGroups?.forEach((optionGroup) => {
              // Skip non-toggle optionGroups when menu is disabled by toggle
              if (
                menuDisabledByToggle &&
                optionGroup.name !== 'toggleOptions'
              ) {
                return
              }
              const defaultOptionName = this.getEffectiveDefault(
                optionGroup,
                file.productSpecs.specs
              )

              if (defaultOptionName) {
                let defaultOption = optionGroup.options?.find(
                  (opt) => opt.name === defaultOptionName
                )

                // If the found option has subOptions, use the first subOption
                // (parent options without priceKey are just containers)
                if (defaultOption?.subOptions?.length > 0) {
                  defaultOption = defaultOption.subOptions[0]
                }

                if (defaultOption) {
                  const newOption = {
                    title: menu.title || '',
                    optionGroupName: optionGroup.name,
                    optionName: defaultOption.name,
                    optionTitle: defaultOption.title,
                    productionTitle: defaultOption.productionTitle || defaultOption.title,
                    priceKey: defaultOption.priceKey,
                    priceValue: defaultOption.priceValue,
                    productionCategory:
                      optionGroup.productionCategory || menu.title
                  }

                  // Add priceObjectKey if the optionGroup has it
                  if (optionGroup?.priceObjectKey) {
                    newOption.priceObjectKey = optionGroup.priceObjectKey
                  }

                  if (menu.fileTypeRequired) {
                    newOption.fileTypeRequired = menu.fileTypeRequired
                  }

                  file.productSpecs.specs.push(newOption)
                }
              }
            })
          }
        }
      })
    },
    getEffectiveDefault(optionGroup, selectedSpecs) {
      // Get the effective default for an optionGroup based on conditionalDefaults
      if (
        !optionGroup.conditionalDefaults ||
        !optionGroup.conditionalDefaults.length
      ) {
        return optionGroup.default
      }

      // Get all currently selected option names
      const selectedOptionNames = selectedSpecs.map((spec) => spec.optionName)

      // Check each conditional default
      for (const conditionalDefault of optionGroup.conditionalDefaults) {
        // If any of the 'when' conditions match current selections
        if (
          conditionalDefault.when.some((optionName) =>
            selectedOptionNames.includes(optionName)
          )
        ) {
          return conditionalDefault.default
        }
      }

      // Fallback to regular default
      return optionGroup.default
    },
    optionSelectedCheck(menu, optionGroupName, option, selectedFiles) {
      let selectedOptionName = ''
      let selectedOptionTitle = ''

      selectedFiles[0].productSpecs.specs.forEach((spec) => {
        if (
          menu.group === spec.group &&
          //menu.groupTitle === spec.groupTitle &&
          menu.title === spec.title &&
          optionGroupName === spec.optionGroupName
        ) {
          selectedOptionName = spec.optionName
          selectedOptionTitle = spec.optionTitle
        }
      })

      //check if menugroup is disabled,then deselect all menu options
      if (
        this.isMenuGroupDisabled(
          optionGroupName,
          selectedFiles[0].productSpecs.specs,
          menu.title
        )
      ) {
        return false
      } else if (
        option.name === selectedOptionName &&
        option.title === selectedOptionTitle
      ) {
        return true
      } else if (
        option.subOptions &&
        option.subOptions.some((opt) => opt.name === selectedOptionName)
      ) {
        return true
      } else {
        return false
      }
    },
    // Check if a product is suitable for the mimeType of the selected files.
    // Products without a fileType key are suitable for every file type.
    // NOTE: not to be confused with menu.fileTypeRequired, which refers to the
    // uploadFileType (ATTACHMENT_FILE / ADDRESS_LIST) instead of the mimeType.
    //
    // Supported notations in the product JSON:
    //   'pdf', 'word', 'excel', 'image'  aliases (see FILE_TYPE_ALIASES)
    //   'image/*'                        wildcard for a whole group
    //   'application/pdf'                full mime type
    productFileTypeCheck(product, selectedFiles) {
      if (!product?.fileType) return true

      const patterns = [].concat(product.fileType)
      if (!patterns.length) return true
      if (!selectedFiles?.length) return true

      // Expand aliases to their mime types
      const mimeTypes = patterns.flatMap(
        (pattern) => FILE_TYPE_ALIASES[pattern] || [pattern]
      )

      // All selected files must be suitable, since a product is applied to all of them
      return selectedFiles.every((file) => {
        // Unknown mimeType should never block selection
        if (!file.mimeType) return true

        return mimeTypes.some((pattern) =>
          pattern.endsWith('/*')
            ? file.mimeType.startsWith(pattern.slice(0, -1))
            : pattern === file.mimeType
        )
      })
    },
    productFileTypeReason(product) {
      if (product?.fileTypeReason) return product.fileTypeReason

      return 'Niet geschikt voor het geselecteerde bestandstype'
    },
    // A product with a quantitySource derives its quantity from the file analysis
    // (e.g. the number of rows in an Excel), so the user cannot change it by hand.
    hasQuantitySource(product) {
      return !!QUANTITY_SOURCES[product?.quantitySource]
    },
    // Quantity for a freshly selected product. Falls back to the regular
    // minQuantity behaviour when the product has no quantitySource, or when the
    // source yields nothing usable: numberOfLines is sent for every file, so a 0
    // means 'not an analysed Excel' rather than 'order zero of these'.
    productQuantity(product, file) {
      const source = QUANTITY_SOURCES[product?.quantitySource]
      const fallback = product?.minQuantity ? product.minQuantity : 1

      if (!source || !file) return fallback

      const quantity = parseInt(source(file))

      return quantity > 0 ? quantity : fallback
    },
    disabledCheck(option, selectedFiles, menu, optionGroupName) {
      //check if menugroup is disabled,then disable all menu options
      if (
        this.isMenuGroupDisabled(
          optionGroupName,
          selectedFiles[0].productSpecs.specs,
          menu.title
        )
      ) {
        return true
      }
      const pagesTotal = selectedFiles[0].pagesColour + selectedFiles[0].pagesBW
      if (pagesTotal < option.minPagesRequired) {
        return true
      }

      // Get active product for parent option matching
      const product = this.getActiveProduct(selectedFiles[0].productSpecs.specs)

      // Get all currently selected option names
      const selectedOptionNames = selectedFiles[0].productSpecs.specs.map(
        (spec) => spec.optionName
      )

      // Check option's own disabledWhen
      if (option.disabledWhen?.length) {
        const isDisabled = option.disabledWhen.some((condition) =>
          selectedOptionNames.some((selectedName) =>
            this.isOptionMatch(condition, selectedName, product)
          )
        )
        if (isDisabled) return true
      }

      // Check if this is a subOption whose parent option has disabledWhen
      if (product) {
        for (const menuDef of product.menus) {
          for (const og of menuDef.optionGroups || []) {
            for (const parentOpt of og.options || []) {
              if (
                parentOpt.subOptions?.some(
                  (sub) => sub.name === option.name
                ) &&
                parentOpt.disabledWhen?.length
              ) {
                const parentDisabled = parentOpt.disabledWhen.some(
                  (condition) =>
                    selectedOptionNames.some((selectedName) =>
                      this.isOptionMatch(condition, selectedName, product)
                    )
                )
                if (parentDisabled) return true
              }
            }
          }
        }
      }

      return false
    },
    hideCheck(option, selectedFiles) {
      if (!option || !selectedFiles?.length) return false

      const selectedSpecs = selectedFiles[0].productSpecs.specs

      // Check option's own hideWhen
      if (option.hideWhen?.length) {
        const isHidden = option.hideWhen.some((condition) =>
          this.parseHideWhenCondition(condition, selectedSpecs)
        )
        if (isHidden) return true
      }

      // Check if this is a subOption whose parent option is hidden
      // This allows putting hideWhen on a parent option to hide all its subOptions
      const product = this.getActiveProduct(selectedSpecs)
      if (product) {
        for (const menu of product.menus) {
          for (const optionGroup of menu.optionGroups || []) {
            for (const parentOpt of optionGroup.options || []) {
              if (
                parentOpt.subOptions?.some((sub) => sub.name === option.name) &&
                parentOpt.hideWhen?.length
              ) {
                const parentHidden = parentOpt.hideWhen.some((condition) =>
                  this.parseHideWhenCondition(condition, selectedSpecs)
                )
                if (parentHidden) return true
              }
            }
          }
        }
      }

      return false
    },
    isMenuGroupDisabled(optionGroupName, selectedSpecs, menuTitle) {
      if (
        optionGroupName != 'toggleOptions' &&
        selectedSpecs.some((spec) => {
          return spec.title === menuTitle && spec.optionName === 'disabled'
        })
      ) {
        return true
      }
    },
    setOption(option, selectedFileNames) {
      // Handle toggleOptions enable/disable before saving the spec, so that
      // productOrderSpecUpdate (which triggers price recalculation) sees the
      // complete updated specs including any defaults added by handleToggleChange.
      if (option.optionGroupName === 'toggleOptions') {
        this.handleToggleChange(option, selectedFileNames)
      }

      this.fileList.productOrderSpecUpdate({
        fileNames: selectedFileNames,
        spec: option,
        hydrate: false
      })

      // After setting an option, check if any currently selected options are now disabled
      // Only run this check if we're not already in the middle of checking disabled options
      if (!this.isCheckingDisabled) {
        this.checkAndFixDisabledOptions(selectedFileNames)
      }

      // After setting an option, check if any currently selected options are now hidden
      // Only run this check if we're not already in the middle of checking hidden options
      if (!this.isCheckingHidden) {
        this.checkAndFixHiddenOptions(selectedFileNames)
      }

      // Remove hidden menu specs and restore visible menu defaults
      // Run in a loop because hiding/showing menus can cascade
      // (e.g. removing Voorkant removes TRANSPARANT_PLASTIC, which makes Omslag visible)
      if (!this.isCheckingHidden) {
        let changed = true
        let maxIterations = 5
        while (changed && maxIterations-- > 0) {
          const specsBefore = JSON.stringify(
            this.fileList.fileList.find((f) =>
              selectedFileNames.includes(f.fileName)
            )?.productSpecs?.specs
          )
          this.checkAndRemoveHiddenMenuSpecs(selectedFileNames)
          this.checkAndRestoreVisibleMenuDefaults(selectedFileNames)
          const specsAfter = JSON.stringify(
            this.fileList.fileList.find((f) =>
              selectedFileNames.includes(f.fileName)
            )?.productSpecs?.specs
          )
          changed = specsBefore !== specsAfter
        }
      }

      // Check if conditional defaults should be applied
      this.applyConditionalDefaults(selectedFileNames)
    },
    handleToggleChange(toggleOption, selectedFileNames) {
      const selectedFiles = this.fileList.fileList.filter((file) =>
        selectedFileNames.includes(file.fileName)
      )
      if (!selectedFiles.length) return

      const file = selectedFiles[0]
      if (!file.productSpecs || !file.productSpecs.specs) return

      const product = this.productOrderStore?.products?.find(
        (p) => p.title === file.productSpecs.title
      )
      if (!product) return

      const menu = product.menus.find(
        (m) => m.title === toggleOption.title
      )
      if (!menu) return

      if (toggleOption.optionName === 'disabled') {
        // Remove all non-toggle specs for this menu
        file.productSpecs.specs = file.productSpecs.specs.filter(
          (spec) =>
            spec.title !== toggleOption.title ||
            spec.optionGroupName === 'toggleOptions'
        )
      } else if (toggleOption.optionName === 'enabled') {
        // Create default specs for all non-toggle optionGroups in this menu
        const pagesTotal =
          file.pagesColour + file.pagesBW

        menu.optionGroups.forEach((optionGroup) => {
          if (optionGroup.name === 'toggleOptions') return

          // Check if spec already exists
          const existingSpec = file.productSpecs.specs.find(
            (spec) =>
              spec.title === menu.title &&
              spec.optionGroupName === optionGroup.name
          )
          if (existingSpec) return

          // Find default option
          let defaultOption = null
          const defaultName = this.getEffectiveDefault(
            optionGroup,
            file.productSpecs.specs
          )

          if (defaultName) {
            for (const option of optionGroup.options) {
              if (option.name === defaultName) {
                if (
                  !option.minPagesRequired ||
                  option.minPagesRequired <= pagesTotal
                ) {
                  // If option has subOptions, use the first subOption as default
                  // (parent options without priceKey are just containers)
                  if (option.subOptions && option.subOptions.length > 0) {
                    defaultOption = option.subOptions[0]
                  } else {
                    defaultOption = option
                  }
                }
                break
              }
              if (option.subOptions) {
                for (const subO of option.subOptions) {
                  if (subO.name === defaultName) {
                    if (
                      !subO.minPagesRequired ||
                      subO.minPagesRequired <= pagesTotal
                    ) {
                      defaultOption = subO
                    }
                    break
                  }
                }
                if (defaultOption) break
              }
            }
          }

          // Fallback to first available option
          if (!defaultOption) {
            for (const option of optionGroup.options) {
              if (
                !option.minPagesRequired ||
                option.minPagesRequired <= pagesTotal
              ) {
                defaultOption = option
                break
              }
            }
          }

          if (defaultOption) {
            const newSpec = {
              title: menu.title || '',
              optionGroupName: optionGroup.name,
              optionName: defaultOption.name,
              optionTitle: defaultOption.title,
              productionTitle:
                defaultOption.productionTitle || defaultOption.title,
              priceKey: defaultOption.priceKey,
              priceValue: defaultOption.priceValue,
              productionCategory:
                optionGroup.productionCategory || menu.title
            }

            if (optionGroup.priceObjectKey) {
              newSpec.priceObjectKey = optionGroup.priceObjectKey
            }

            if (menu.fileTypeRequired) {
              newSpec.fileTypeRequired = menu.fileTypeRequired
            }

            file.productSpecs.specs.push(newSpec)
          }
        })
      }
    },
    applyConditionalDefaults(selectedFileNames) {
      // Apply conditional defaults based on current selections
      const selectedFiles = this.fileList.fileList.filter((file) =>
        selectedFileNames.includes(file.fileName)
      )

      if (!selectedFiles.length) return

      const file = selectedFiles[0]
      if (!file.productSpecs || !file.productSpecs.specs) return

      // Get the product configuration from the store
      if (!this.productOrderStore) return

      const product = this.productOrderStore.products?.find(
        (p) => p.title === file.productSpecs.title
      )
      if (!product) return

      // For each menu
      product.menus.forEach((menu) => {
        menu.optionGroups?.forEach((optionGroup) => {
          // Check if this optionGroup has conditional defaults
          if (
            !optionGroup.conditionalDefaults ||
            !optionGroup.conditionalDefaults.length
          ) {
            return
          }

          // Get the effective default based on current selections
          const effectiveDefault = this.getEffectiveDefault(
            optionGroup,
            file.productSpecs.specs
          )

          // Find the current spec for this optionGroup
          const currentSpec = file.productSpecs.specs.find(
            (spec) =>
              spec.title === (menu.title || '') &&
              spec.optionGroupName === optionGroup.name
          )

          // If current spec doesn't match effective default, update it
          if (currentSpec && currentSpec.optionName !== effectiveDefault) {
            const newDefaultOption = optionGroup.options?.find(
              (opt) => opt.name === effectiveDefault
            )

            if (newDefaultOption) {
              const newOption = {
                title: menu.title || '',
                optionGroupName: optionGroup.name,
                optionName: newDefaultOption.name,
                optionTitle: newDefaultOption.title,
                productionTitle: newDefaultOption.productionTitle || newDefaultOption.title,
                priceKey: newDefaultOption.priceKey,
                priceValue: newDefaultOption.priceValue,
                productionCategory: optionGroup.productionCategory || menu.title
              }

              // Add priceObjectKey if the optionGroup has it
              if (optionGroup?.priceObjectKey) {
                newOption.priceObjectKey = optionGroup.priceObjectKey
              }

              if (menu.fileTypeRequired) {
                newOption.fileTypeRequired = menu.fileTypeRequired
              }

              this.fileList.productOrderSpecUpdate({
                fileNames: selectedFileNames,
                spec: newOption,
                hydrate: false
              })
            }
          }
        })
      })
    },
    checkAndFixDisabledOptions(selectedFileNames) {
      // Prevent infinite loops
      if (this.isCheckingDisabled) return
      this.isCheckingDisabled = true
      // Get the selected files
      const selectedFiles = this.fileList.fileList.filter((file) =>
        selectedFileNames.includes(file.fileName)
      )

      if (!selectedFiles.length) return

      const file = selectedFiles[0]
      if (!file.productSpecs || !file.productSpecs.specs) return

      // Get all currently selected option names
      const selectedOptionNames = file.productSpecs.specs.map(
        (spec) => spec.optionName
      )

      // Get the product configuration from the store
      if (!this.productOrderStore) return

      const product = this.productOrderStore.products?.find(
        (p) => p.title === file.productSpecs.title
      )
      if (!product) return

      // Check each spec to see if it should be excluded
      file.productSpecs.specs.forEach((currentSpec) => {
        // Find the corresponding menu and option configuration
        product.menus.forEach((menu) => {
          menu.optionGroups?.forEach((optionGroup) => {
            // Match the spec to its option configuration
            if (
              currentSpec.title === (menu.title || '') &&
              currentSpec.optionGroupName === optionGroup.name
            ) {
              // Find the option definition - could be a main option or a subOption
              let optionDef = optionGroup.options?.find(
                (opt) => opt.name === currentSpec.optionName
              )

              let parentOption = null

              // If not found in main options, check subOptions
              if (!optionDef) {
                optionGroup.options?.forEach((opt) => {
                  if (opt.subOptions) {
                    const subOpt = opt.subOptions.find(
                      (sub) => sub.name === currentSpec.optionName
                    )
                    if (subOpt) {
                      optionDef = subOpt
                      parentOption = opt
                    }
                  }
                })
              }

              // Collect disabledWhen conditions from option itself AND from parent option
              const disableConditions = [...(optionDef?.disabledWhen || [])]
              if (parentOption?.disabledWhen?.length) {
                disableConditions.push(...parentOption.disabledWhen)
              }

              if (optionDef && disableConditions.length > 0) {
                // Check if this option should be disabled based on currently selected options
                // Supports parent option names (matches if any subOption is selected)
                // Also inherits disabledWhen from parent option
                const shouldBeDisabled = selectedOptionNames.some(
                  (selectedName) =>
                    disableConditions.some((condition) =>
                      this.isOptionMatch(condition, selectedName, product)
                    )
                )

                if (shouldBeDisabled) {
                  let replacementOption = null

                  // Helper to check if an option is currently disabled
                  // Also checks if the option's parent is disabled
                  const isOptDisabled = (opt) => {
                    if (
                      opt.disabledWhen?.some((condition) =>
                        selectedOptionNames.some((selectedName) =>
                          this.isOptionMatch(condition, selectedName, product)
                        )
                      )
                    ) {
                      return true
                    }
                    // Check if this opt is a subOption whose parent is disabled
                    for (const parentOpt of optionGroup.options || []) {
                      if (
                        parentOpt.subOptions?.some(
                          (sub) => sub.name === opt.name
                        ) &&
                        parentOpt.disabledWhen?.some((condition) =>
                          selectedOptionNames.some((selectedName) =>
                            this.isOptionMatch(
                              condition,
                              selectedName,
                              product
                            )
                          )
                        )
                      ) {
                        return true
                      }
                    }
                    return false
                  }

                  // Step 1: Try to find option with same title (within subOptions if applicable)
                  if (parentOption && parentOption.subOptions) {
                    replacementOption = parentOption.subOptions.find(
                      (subOpt) =>
                        subOpt &&
                        subOpt.title === optionDef.title &&
                        subOpt.name !== optionDef.name &&
                        !isOptDisabled(subOpt)
                    )
                  } else {
                    replacementOption = optionGroup.options.find(
                      (opt) =>
                        opt &&
                        opt.title === optionDef.title &&
                        opt.name !== optionDef.name &&
                        !isOptDisabled(opt)
                    )
                  }

                  // Step 2: Try default option if available
                  if (!replacementOption && optionGroup.default) {
                    replacementOption = optionGroup.options.find(
                      (opt) =>
                        opt &&
                        opt.name === optionGroup.default &&
                        !isOptDisabled(opt)
                    )
                  }

                  // Step 3: Fallback to first available option in the options array
                  if (!replacementOption) {
                    const firstAvailableOption = optionGroup.options.find(
                      (opt) =>
                        opt &&
                        opt.name !== optionDef.name &&
                        !isOptDisabled(opt)
                    )

                    // If the first available option has subOptions, select the first available subOption
                    if (firstAvailableOption?.subOptions) {
                      replacementOption = firstAvailableOption.subOptions.find(
                        (subOpt) => subOpt && !isOptDisabled(subOpt)
                      )
                    } else {
                      replacementOption = firstAvailableOption
                    }
                  }

                  if (replacementOption) {
                    // Replace the disabled option
                    const newOption = {
                      title: menu.title || '',
                      optionGroupName: optionGroup.name,
                      optionName: replacementOption.name,
                      optionTitle: replacementOption.title,
                      productionTitle: replacementOption.productionTitle || replacementOption.title,
                      priceKey: replacementOption.priceKey,
                      priceValue: replacementOption.priceValue,
                      productionCategory:
                        optionGroup.productionCategory || menu.title
                    }

                    // Add priceObjectKey if the optionGroup has it
                    if (optionGroup?.priceObjectKey) {
                      newOption.priceObjectKey = optionGroup.priceObjectKey
                    }

                    // Add fileTypeRequired if the menu has it
                    if (menu.fileTypeRequired) {
                      newOption.fileTypeRequired = menu.fileTypeRequired
                    }

                    // Update without triggering another check (prevent infinite loop)
                    this.fileList.productOrderSpecUpdate({
                      fileNames: selectedFileNames,
                      spec: newOption,
                      hydrate: false
                    })
                  }
                }
              }
            }
          })
        })
      })

      // Reset the flag
      this.isCheckingDisabled = false
    },
    checkAndFixHiddenOptions(selectedFileNames) {
      // Prevent infinite loops
      if (this.isCheckingHidden) return
      this.isCheckingHidden = true

      // Get the selected files
      const selectedFiles = this.fileList.fileList.filter((file) =>
        selectedFileNames.includes(file.fileName)
      )

      if (!selectedFiles.length) {
        this.isCheckingHidden = false
        return
      }

      const file = selectedFiles[0]
      if (!file.productSpecs || !file.productSpecs.specs) {
        this.isCheckingHidden = false
        return
      }

      // Get all currently selected option names
      const selectedOptionNames = file.productSpecs.specs.map(
        (spec) => spec.optionName
      )

      // Get the product configuration from the store
      if (!this.productOrderStore) {
        this.isCheckingHidden = false
        return
      }

      const product = this.productOrderStore.products?.find(
        (p) => p.title === file.productSpecs.title
      )
      if (!product) {
        this.isCheckingHidden = false
        return
      }

      // Check each spec to see if it should be hidden
      file.productSpecs.specs.forEach((currentSpec) => {
        // Find the corresponding menu and option configuration
        product.menus.forEach((menu) => {
          menu.optionGroups?.forEach((optionGroup) => {
            // Match the spec to its option configuration
            if (
              currentSpec.title === (menu.title || '') &&
              currentSpec.optionGroupName === optionGroup.name
            ) {
              // Special handling for datePicker optionGroups
              if (optionGroup.name === 'datePicker') {
                // Check if the datepicker's first option has hideWhen
                const datePickerOption = optionGroup.options?.[0]
                if (
                  datePickerOption &&
                  datePickerOption.hideWhen &&
                  datePickerOption.hideWhen.length > 0
                ) {
                  const shouldBeHidden = selectedOptionNames.some(
                    (selectedName) =>
                      datePickerOption.hideWhen.some((condition) =>
                        this.isOptionMatch(condition, selectedName, product)
                      )
                  )

                  if (shouldBeHidden) {
                    // Remove the datepicker spec by setting optionName to NONE
                    const emptyOption = {
                      title: menu.title || '',
                      optionGroupName: optionGroup.name,
                      optionName: 'NONE',
                      optionTitle: '',
                      priceKey: 'datePicker',
                      productionCategory:
                        optionGroup.productionCategory || menu.title
                    }

                    // Add priceObjectKey if the optionGroup has it
                    if (optionGroup?.priceObjectKey) {
                      emptyOption.priceObjectKey = optionGroup.priceObjectKey
                    }

                    if (menu.fileTypeRequired) {
                      emptyOption.fileTypeRequired = menu.fileTypeRequired
                    }

                    this.fileList.productOrderSpecUpdate({
                      fileNames: selectedFileNames,
                      spec: emptyOption,
                      hydrate: false
                    })
                  }
                }
                return
              }

              // Find the option definition - could be a main option or a subOption
              let optionDef = optionGroup.options?.find(
                (opt) => opt.name === currentSpec.optionName
              )

              let parentOption = null

              // If not found in main options, check subOptions
              if (!optionDef) {
                optionGroup.options?.forEach((opt) => {
                  if (opt.subOptions) {
                    const subOpt = opt.subOptions.find(
                      (sub) => sub.name === currentSpec.optionName
                    )
                    if (subOpt) {
                      optionDef = subOpt
                      parentOption = opt
                    }
                  }
                })
              }

              // Collect hideWhen conditions from option itself AND from parent option
              const hideConditions = [...(optionDef?.hideWhen || [])]
              if (parentOption?.hideWhen?.length) {
                hideConditions.push(...parentOption.hideWhen)
              }

              if (optionDef && hideConditions.length > 0) {
                // Check if this option should be hidden based on currently selected options
                // Supports parent option names (matches if any subOption is selected)
                // Also inherits hideWhen from parent option
                const shouldBeHidden = selectedOptionNames.some(
                  (selectedName) =>
                    hideConditions.some((condition) =>
                      this.isOptionMatch(condition, selectedName, product)
                    )
                )

                if (shouldBeHidden) {
                  let replacementOption = null

                  // Helper to check if an option is currently hidden
                  // Also checks if the option's parent is hidden
                  const isOptHidden = (opt) => {
                    if (
                      opt.hideWhen?.some((condition) =>
                        selectedOptionNames.some((selectedName) =>
                          this.isOptionMatch(condition, selectedName, product)
                        )
                      )
                    ) {
                      return true
                    }
                    // Check if this opt is a subOption whose parent is hidden
                    for (const parentOpt of optionGroup.options || []) {
                      if (
                        parentOpt.subOptions?.some(
                          (sub) => sub.name === opt.name
                        ) &&
                        parentOpt.hideWhen?.some((condition) =>
                          selectedOptionNames.some((selectedName) =>
                            this.isOptionMatch(
                              condition,
                              selectedName,
                              product
                            )
                          )
                        )
                      ) {
                        return true
                      }
                    }
                    return false
                  }

                  // Step 1: Try to find option with same title (within subOptions if applicable)
                  if (parentOption && parentOption.subOptions) {
                    replacementOption = parentOption.subOptions.find(
                      (subOpt) =>
                        subOpt &&
                        subOpt.title === optionDef.title &&
                        subOpt.name !== optionDef.name &&
                        !isOptHidden(subOpt)
                    )
                  } else {
                    replacementOption = optionGroup.options.find(
                      (opt) =>
                        opt &&
                        opt.title === optionDef.title &&
                        opt.name !== optionDef.name &&
                        !isOptHidden(opt)
                    )
                  }

                  // Step 2: Try default option if available
                  if (!replacementOption && optionGroup.default) {
                    replacementOption = optionGroup.options.find(
                      (opt) =>
                        opt &&
                        opt.name === optionGroup.default &&
                        !isOptHidden(opt)
                    )
                  }

                  // Step 3: Fallback to first available option in the options array
                  if (!replacementOption) {
                    const firstAvailableOption = optionGroup.options.find(
                      (opt) =>
                        opt &&
                        opt.name !== optionDef.name &&
                        !isOptHidden(opt)
                    )

                    // If the first available option has subOptions, select the first available subOption
                    if (firstAvailableOption?.subOptions) {
                      replacementOption = firstAvailableOption.subOptions.find(
                        (subOpt) => subOpt && !isOptHidden(subOpt)
                      )
                    } else {
                      replacementOption = firstAvailableOption
                    }
                  }

                  if (replacementOption) {
                    // Replace the hidden option
                    const newOption = {
                      title: menu.title || '',
                      optionGroupName: optionGroup.name,
                      optionName: replacementOption.name,
                      optionTitle: replacementOption.title,
                      productionTitle: replacementOption.productionTitle || replacementOption.title,
                      priceKey: replacementOption.priceKey,
                      priceValue: replacementOption.priceValue,
                      productionCategory:
                        optionGroup.productionCategory || menu.title
                    }

                    // Add priceObjectKey if the optionGroup has it
                    if (optionGroup?.priceObjectKey) {
                      newOption.priceObjectKey = optionGroup.priceObjectKey
                    }

                    // Add fileTypeRequired if the menu has it
                    if (menu.fileTypeRequired) {
                      newOption.fileTypeRequired = menu.fileTypeRequired
                    }

                    // Update without triggering another check (prevent infinite loop)
                    this.fileList.productOrderSpecUpdate({
                      fileNames: selectedFileNames,
                      spec: newOption,
                      hydrate: false
                    })
                  }
                }
              }
            }
          })
        })
      })

      // Reset the flag
      this.isCheckingHidden = false
    },
    deSelectSpec(
      menu,
      optionGroupName,
      optionName,
      options,
      selectedFileNames
    ) {
      const newOption =
        options.find((opt) => opt.name === 'NONE') ||
        options.find((opt) => opt.name != optionName)

      // Find the optionGroup to get productionCategory
      const optionGroup = menu.optionGroups?.find(
        (og) => og.name === optionGroupName
      )

      const selectedOption = {
        title: menu.title || '',
        optionGroupName: optionGroupName || '',
        optionName: newOption.name,
        optionTitle: newOption.title,
        productionTitle: newOption.productionTitle || newOption.title,
        priceKey: newOption.priceKey,
        priceValue: newOption.priceValue,
        productionCategory: optionGroup?.productionCategory || menu.title
      }

      // Add priceObjectKey if the optionGroup has it
      if (optionGroup?.priceObjectKey) {
        selectedOption.priceObjectKey = optionGroup.priceObjectKey
      }

      // Add fileTypeRequired if the menu has it
      if (menu.fileTypeRequired) {
        selectedOption.fileTypeRequired = menu.fileTypeRequired
      }

      this.setOption(selectedOption, selectedFileNames)
    }
  }
}
